import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { formatWhatsApp } from "@/lib/utils";
import { ChevronDown } from "lucide-react";

const COUNTRIES = [
  { code: "55", flag: "🇧🇷", name: "Brasil", mask: "(00) 0 0000-0000" },
  { code: "1", flag: "🇺🇸", name: "EUA", mask: "(000) 000-0000" },
  { code: "351", flag: "🇵🇹", name: "Portugal", mask: "000 000 000" },
  { code: "54", flag: "🇦🇷", name: "Argentina", mask: "00 0000-0000" },
  { code: "595", flag: "🇵🇾", name: "Paraguai", mask: "000 000 000" },
  { code: "598", flag: "🇺🇾", name: "Uruguai", mask: "00 000 000" },
  { code: "56", flag: "🇨🇱", name: "Chile", mask: "00 0000 0000" },
  { code: "57", flag: "🇨🇴", name: "Colômbia", mask: "000 000 0000" },
  { code: "34", flag: "🇪🇸", name: "Espanha", mask: "000 00 00 00" },
  { code: "39", flag: "🇮🇹", name: "Itália", mask: "000 000 0000" },
  { code: "44", flag: "🇬🇧", name: "Reino Unido", mask: "0000 000000" },
  { code: "49", flag: "🇩🇪", name: "Alemanha", mask: "000 00000000" },
  { code: "33", flag: "🇫🇷", name: "França", mask: "0 00 00 00 00" },
  { code: "81", flag: "🇯🇵", name: "Japão", mask: "00 0000 0000" },
];

interface WhatsAppInputProps {
  value: string;
  onChange: (value: string) => void;
  ddi: string;
  onDdiChange: (ddi: string) => void;
  className?: string;
  id?: string;
  required?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}

export function WhatsAppInput({
  value,
  onChange,
  ddi,
  onDdiChange,
  className = "",
  id,
  required,
  autoFocus,
  placeholder,
  onKeyDown,
  disabled = false,
}: WhatsAppInputProps) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selected = COUNTRIES.find((c) => c.code === ddi) || COUNTRIES[0];
  const rawDigits = value.replace(/\D/g, "");
  const hideDdi = rawDigits.length > 11 && ddi === "55";

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (ddi === "55") {
      onChange(formatWhatsApp(e.target.value));
    } else {
      const digits = e.target.value.replace(/[^\d\s()-]/g, "");
      onChange(digits);
    }
  };

  return (
    <div className={`flex relative rounded-md ${focused ? "ring-2 ring-ring ring-offset-0" : ""}`}>
      <div ref={dropdownRef} className={`relative ${hideDdi ? "hidden" : ""}`}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(!open)}
          className={`flex items-center justify-center gap-1 px-3 border border-r-0 border-input rounded-l-md bg-muted hover:bg-muted/80 transition-colors text-sm shrink-0 h-full ${focused ? "border-ring" : ""}`}
        >
          <span className="text-lg leading-none">{selected.flag}</span>
          <span className="text-muted-foreground text-xs">+{selected.code}</span>
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>
        {open && (
          <div className="absolute top-full left-0 z-50 mt-1 w-56 max-h-60 overflow-y-auto bg-popover border border-border rounded-lg shadow-lg">
            {COUNTRIES.map((country) => (
              <button
                key={country.code}
                type="button"
                onClick={() => {
                  onDdiChange(country.code);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-accent transition-colors text-left ${
                  country.code === ddi ? "bg-accent" : ""
                }`}
              >
                <span className="text-lg">{country.flag}</span>
                <span className="flex-1">{country.name}</span>
                <span className="text-muted-foreground">+{country.code}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <Input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={value}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={onKeyDown}
        className={`${hideDdi ? "rounded-md" : "rounded-l-none"} focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input ${className}`}
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        placeholder={placeholder || (ddi === "55" ? "(00) 0 0000-0000" : selected.mask)}
      />
    </div>
  );
}
